import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../apiHelper';
import { useEdit, useFetch } from '../react-query';

const SURVEY_REMINDER_CACHE_PREFIX = 'deepecho.survey-reminder';
const SURVEY_REMINDER_REQUERY_BUFFER_MS = 1000;

export interface LocalizedText {
  [key: string]: string;
}

export interface SurveyQuestion {
  id: string;
  type: string;
  prompt: LocalizedText;
  required: boolean;
}

export interface SurveySection {
  id: string;
  title?: LocalizedText | null;
  description?: LocalizedText | null;
  comment_prompt?: LocalizedText | null;
  questions: SurveyQuestion[];
}

export interface SurveySubmission {
  id: string;
  survey_id: string;
  user_id: string;
  cycle_key?: string | null;
  answers: Record<string, number>;
  section_comments: Record<string, string>;
  submitted_at: string;
  updated_at?: string | null;
}

export interface SurveySummary {
  id: string;
  slug: string;
  schedule_type: 'one_time' | 'recurring';
  cadence_days?: number | null;
  title: LocalizedText;
  description: LocalizedText;
  question_count: number;
  is_due: boolean;
  status: 'pending' | 'completed';
  next_due_at?: string | null;
  last_submitted_at?: string | null;
  current_response?: SurveySubmission | null;
}

export interface SurveyReminderPrimary {
  id: string;
  slug: string;
  title: LocalizedText;
  description: LocalizedText;
  question_count: number;
}

export interface SurveyReminder {
  has_due_surveys: boolean;
  due_count: number;
  primary_survey?: SurveyReminderPrimary | null;
  next_due_at?: string | null;
  checked_at: string;
  next_check_at: string;
}

export interface SurveyDetail extends SurveySummary {
  sections: SurveySection[];
}

interface ApiResponse<T> {
  data: T;
}

export interface SubmitSurveyPayload {
  surveyId: string;
  answers: Record<string, number>;
  sectionComments: Record<string, string>;
}

export const surveysQueryKeys = {
  all: ['surveys'],
  due: ['surveys', 'due'],
  reminderAll: ['surveys', 'reminder'],
  reminder: (userId: string) => ['surveys', 'reminder', userId],
  detail: (surveyId: string) => ['surveys', surveyId],
};

export function localizeSurveyText(text: LocalizedText | null | undefined, language: string): string {
  if (!text) {
    return '';
  }
  if (text[language]) {
    return text[language];
  }
  if (language.startsWith('fr') && text.fr) {
    return text.fr;
  }
  return text['en-US'] || Object.values(text)[0] || '';
}

function buildSurveyReminderCacheKey(userId: string): string {
  return `${SURVEY_REMINDER_CACHE_PREFIX}:${userId}`;
}

function parseTimestamp(value?: string | null): number | null {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readCachedSurveyReminder(userId: string): SurveyReminder | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const cacheKey = buildSurveyReminderCacheKey(userId);

  try {
    const rawValue = window.localStorage.getItem(cacheKey);
    if (!rawValue) {
      return undefined;
    }

    const parsed = JSON.parse(rawValue) as SurveyReminder;
    const nextCheckAt = parseTimestamp(parsed.next_check_at);
    if (!nextCheckAt || nextCheckAt <= Date.now()) {
      window.localStorage.removeItem(cacheKey);
      return undefined;
    }

    return parsed;
  } catch {
    window.localStorage.removeItem(cacheKey);
    return undefined;
  }
}

function writeCachedSurveyReminder(userId: string, reminder: SurveyReminder): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(buildSurveyReminderCacheKey(userId), JSON.stringify(reminder));
}

export function clearSurveyReminderCache(userId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(buildSurveyReminderCacheKey(userId));
}

export function clearAllSurveyReminderCaches(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const keysToRemove: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(SURVEY_REMINDER_CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

export function useSurveys(dueOnly = false) {
  const getSurveysFn = async (): Promise<SurveySummary[]> => {
    const suffix = dueOnly ? '?due_only=true' : '';
    const response = (await apiClient.get(`/surveys${suffix}`)) as ApiResponse<SurveySummary[]>;
    return response?.data ?? [];
  };

  return useFetch(getSurveysFn, dueOnly ? surveysQueryKeys.due : surveysQueryKeys.all);
}

export function useSurveyReminder(userId?: string) {
  const queryClient = useQueryClient();
  const cachedReminder = useMemo(
    () => (userId ? readCachedSurveyReminder(userId) : undefined),
    [userId]
  );
  const initialDataUpdatedAt = parseTimestamp(cachedReminder?.checked_at) ?? undefined;
  const staleTime = cachedReminder
    ? Math.max(
        0,
        (parseTimestamp(cachedReminder.next_check_at) ?? Date.now()) - (initialDataUpdatedAt ?? Date.now())
      )
    : 0;

  const getSurveyReminderFn = async (): Promise<SurveyReminder> => {
    const response = (await apiClient.get('/surveys/reminder')) as ApiResponse<SurveyReminder>;
    if (!response?.data) {
      throw new Error('Missing survey reminder payload');
    }
    if (userId) {
      writeCachedSurveyReminder(userId, response.data);
    }
    return response.data;
  };

  const query = useFetch(
    getSurveyReminderFn,
    userId ? surveysQueryKeys.reminder(userId) : ['surveys', 'reminder', 'missing-user'],
    {
      enabled: Boolean(userId),
      initialData: cachedReminder,
      initialDataUpdatedAt,
      staleTime,
    }
  );

  useEffect(() => {
    if (!userId || !query.data?.next_check_at) {
      return;
    }

    const nextCheckAt = parseTimestamp(query.data.next_check_at);
    if (!nextCheckAt) {
      return;
    }

    const delay = Math.max(0, nextCheckAt - Date.now()) + SURVEY_REMINDER_REQUERY_BUFFER_MS;
    const timerId = window.setTimeout(() => {
      clearSurveyReminderCache(userId);
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.reminder(userId) });
    }, delay);

    return () => window.clearTimeout(timerId);
  }, [userId, query.data?.next_check_at, queryClient]);

  return query;
}

export function useSurvey(surveyId?: string) {
  const getSurveyFn = async (): Promise<SurveyDetail | null> => {
    if (!surveyId) {
      return null;
    }
    const response = (await apiClient.get(`/surveys/${surveyId}`)) as ApiResponse<SurveyDetail>;
    return response?.data ?? null;
  };

  return useFetch(getSurveyFn, surveyId ? surveysQueryKeys.detail(surveyId) : ['surveys', 'missing-detail'], {
    enabled: Boolean(surveyId),
  });
}

export function useSubmitSurvey(surveyId: string) {
  const submitSurveyFn = async ({ answers, sectionComments }: Omit<SubmitSurveyPayload, 'surveyId'>) => {
    const response = (await apiClient.post(`/surveys/${surveyId}/submit`, {
      answers,
      section_comments: sectionComments,
    })) as ApiResponse<SurveySubmission>;
    return response?.data;
  };

  return useEdit(
    submitSurveyFn,
    [
      surveysQueryKeys.all,
      surveysQueryKeys.due,
      surveysQueryKeys.reminderAll,
      surveysQueryKeys.detail(surveyId),
    ],
    'surveys.submitFailed',
    'surveys.submitted',
    () => {
      clearAllSurveyReminderCaches();
    }
  );
}
